import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  AiFillGithub,
  AiFillLinkedin,
  AiOutlineMail,
  AiOutlineClose,
  AiOutlineTwitter,
} from "react-icons/ai";
import { SiMedium } from "react-icons/si";
import { HiMenuAlt3 } from "react-icons/hi";

const RESUME_URL =
  "https://docs.google.com/document/d/1o7UQG8fySF2Uu82c19je_sVvQKJoxDqcnenoOPVNIH8/edit?usp=sharing";

const navItems = [
  { to: "/", label: "About", num: "01", end: true },
  { to: "/experience", label: "Experience", num: "02" },
  { to: "/projects", label: "Projects", num: "03" },
  { to: "/writing", label: "Writing", num: "04" },
];

const socials = [
  { href: "https://github.com/bolu1", icon: AiFillGithub, label: "GitHub" },
  {
    href: "https://linkedin.com/in/adetifa-boluwatife-819a65201",
    icon: AiFillLinkedin,
    label: "LinkedIn",
  },
  { href: "https://twitter.com/AdetifaBolu", icon: AiOutlineTwitter, label: "X" },
  {
    href: "https://medium.com/@adetifaboluwatife",
    icon: SiMedium,
    label: "Medium",
  },
  { href: "mailto:boluadetifa@gmail.com", icon: AiOutlineMail, label: "Email" },
];

const navLinkClass = ({ isActive }) =>
  `flex items-center gap-3 pl-3 pr-3 py-2 -ml-3 rounded-md border-l-2 font-mono text-sm tracking-wide transition-colors ${
    isActive
      ? "text-accent border-accent bg-accent/[0.06]"
      : "text-muted border-transparent hover:text-white hover:border-border"
  }`;

const SidebarContent = ({ onNavigate }) => (
  <div className="flex flex-col h-full">
    <div>
      <h1 className="text-2xl font-bold text-white">Bolu Adetifa</h1>
      <p className="text-accent font-mono text-sm mt-1">Software Engineer</p>
      <p className="text-muted text-sm mt-3 leading-relaxed">
        Building scalable backend systems and full-stack products with Rust,
        TypeScript, and Node.js.
      </p>
    </div>

    <nav className="mt-10 flex flex-col gap-1">
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={navLinkClass}
        >
          <span className="text-xs text-border">{item.num}</span>
          {item.label}
        </NavLink>
      ))}
    </nav>

    <a
      href={RESUME_URL}
      target="_blank"
      rel="noreferrer"
      className="mt-8 inline-flex items-center justify-center px-4 py-2.5 rounded-md border border-border text-sm text-white hover:border-accent hover:text-accent transition-colors font-mono"
    >
      Resume ↗
    </a>

    <div className="mt-auto pt-10 flex gap-3">
      {socials.map(({ href, icon: Icon, label }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noreferrer"
          aria-label={label}
          className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-muted hover:text-accent hover:border-accent transition-colors"
        >
          <Icon className="w-4 h-4" />
        </a>
      ))}
    </div>
  </div>
);

const AppShell = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="min-h-screen bg-ink text-white">
      {/* Mobile top bar */}
      <header className="lg:hidden flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-ink z-40">
        <span className="font-mono text-sm text-accent">bolu.dev</span>
        <button
          onClick={() => setIsOpen(true)}
          aria-label="Open menu"
          className="text-white"
        >
          <HiMenuAlt3 className="w-6 h-6" />
        </button>
      </header>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed inset-0 z-50 bg-ink p-6 lg:hidden overflow-y-auto"
          >
            <button
              onClick={() => setIsOpen(false)}
              aria-label="Close menu"
              className="absolute top-5 right-5 text-white"
            >
              <AiOutlineClose className="w-6 h-6" />
            </button>
            <div className="mt-14 h-[calc(100%-3.5rem)]">
              <SidebarContent onNavigate={() => setIsOpen(false)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="lg:flex lg:max-w-[88rem] lg:mx-auto">
        <aside className="hidden lg:flex lg:flex-col lg:w-[22rem] lg:shrink-0 lg:h-screen lg:sticky lg:top-0 border-r border-border px-10 py-14">
          <SidebarContent />
        </aside>

        <main className="flex-1 min-w-0 px-5 py-10 lg:px-16 lg:py-16">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AppShell;
